import { Component, inject, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { DSM3NavItem } from '@eredes/dsm3';
import { DSM34Dialog } from '@eredes/dsm34';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { RequestPermission } from '../../_core/models/request-permission.model';
import { ProfileService } from '../../_core/services/profile.service';
import { UserService } from '../../_core/services/user.service';
import { GenericModalComponent } from '../../_shared/generic-modal/generic-modal.component';
import { AuthService } from '../../auth/auth.service';
import { RequestModalComponent } from '../request-modal/request-modal.component';
import { RequestPermissionService } from '../request-permission.service';
import { RequestService } from '../request.service';

import { RequestDetail } from './request-detail.model';

import { GoogleAnalyticsEventsService } from 'app/_core/services/google-analytics-events.service';

@Component({
  selector: 'app-request-detail-old',
  templateUrl: './request-detail-old.component.html',
  standalone: false,
})
export class RequestDetailOldComponent implements OnInit {
  breadcrumbsItems$: Subject<DSM3NavItem[]> = new BehaviorSubject<DSM3NavItem[]>([]);

  request: RequestDetail;
  sendCommentForm: UntypedFormGroup;
  sendInternalCommentForm: UntypedFormGroup;
  user: any;
  isRet: boolean;
  dataRequestModal: any;
  dataReOpenRequestModal: any;
  dataGenericModal: any;
  requestType: string;
  requestTitle: string;
  requestOperationType: string;
  writePermission: RequestPermission;
  createPermission: RequestPermission;
  internalNotesPermission: RequestPermission;
  permissions: string[] = [];
  fileList: any[] = [];
  requestAttachments: any[] = [];
  fileTooLarge: boolean;

  public dialog = inject(DSM34Dialog);

  constructor(
    public fb: UntypedFormBuilder,
    public authService: AuthService,
    protected requestService: RequestService,
    protected userService: UserService,
    protected profileService: ProfileService,
    protected requestPermissionService: RequestPermissionService,
    protected route: ActivatedRoute,
    protected router: Router
  ) {}

  private setBreadcrumb(): void {
    const breadcrumbsItems: DSM3NavItem[] = [
      {
        label: 'Pedidos',
        href: '/request',
      },
      {
        label: `${this.requestTitle}`,
        href: this.getRouteBreadcrumb(),
      },
      {
        label: 'Detalhe Pedido',
        href: this.getRouteBreadcrumb() + '/' + this.request.detail.requestUniqueCode,
      },
    ];
    this.breadcrumbsItems$.next(breadcrumbsItems);
  }

  private getRouteBreadcrumb() {
    if (!this.isRet) {
      return '/request/request-edp';
    } else if (this.requestOperationType === 'pt.edp.request.operation.1') {
      return '/request/request-operation';
    } else if (this.requestOperationType === 'pt.edp.request.operation.2') {
      return '/request/request-clarification';
    }
  }

  ngOnInit() {
    this.user = this.userService.getUser();
    this.isRet = this.userService.isProfileRetailer();

    this.sendCommentForm = this.fb.group({
      comment: ['', [Validators.required, Validators.maxLength(3500)]],
      file: '',
    });
    this.sendInternalCommentForm = this.fb.group({
      internalnotes: ['', [Validators.required, Validators.maxLength(3500)]],
    });

    this.route.params.subscribe(
      (data) => {
        this.requestService.getRequestDetails(data.id, this.isRet, this.userService.isProfileRealocater()).subscribe(
          (res: any) => {
            this.request = new RequestDetail();
            this.request.detail = res.Result.Collection.detail;
            this.request.fields = res.Result.Collection.fields || [];
            this.request.comments = res.Result.Collection.comments || [];
            this.request.internalComments = res.Result.Collection.commentsInternal || [];
            this.request.realocatable = res.Result.Collection.isRealocatable || false;
            this.request.possibleStatus = res.Result.Collection.possibleStatus || [];

            this.requestService.getRequestAttachments(this.request.detail.requestUniqueCode).subscribe((files) => {
              this.requestAttachments = files.Result.Collection;
            });

            this.route.data.subscribe((routeData) => {
              this.requestType = routeData.requestType;

              // Remove from string 'Pedido de ' and capitalize string
              const title = this.request.detail.requestOperationType.replace('Pedido de ', '');
              const titleCapitalized = title.charAt(0).toUpperCase() + title.slice(1);
              this.requestTitle = routeData.requestTitle || titleCapitalized;

              this.requestOperationType = routeData.requestOperationType;
              if (
                ['pt.edp.request.operation.1', 'pt.edp.request.operation.2'].includes(routeData.requestOperationType)
              ) {
                this.requestPermissionService
                  .getRequestPermissions(this.request.detail.requestAreaUniqueCode)
                  .pipe(
                    map((permissions) => permissions.map((permission) => permission.permissionName)),
                    tap((permissions) =>
                      this.setPermissions(
                        permissions.filter((permissionName) => permissionName.match('_create_')),
                        permissions.filter((permissionName) => permissionName.match('_write_'))
                      )
                    )
                  )
                  .subscribe();
              } else {
                this.requestPermissionService.getPoAndPeWritePermissions().subscribe((permissionData) => {
                  this.setPermissions([], permissionData[0].concat(permissionData[1]) || []);
                });
              }
              this.setBreadcrumb();

              if (
                this.requestOperationType === 'pt.edp.request.operation.1' ||
                this.request.detail.requestOperationTypeUniqueCode === 'pt.edp.request.operation.1'
              ) {
                GoogleAnalyticsEventsService.emitEvent(
                  'Pedidos',
                  'Detalhe Pedido de Operação',
                  'Acede ao detalhe de um Pedido de Operação'
                );
              } else if (
                this.requestOperationType === 'pt.edp.request.operation.2' ||
                this.request.detail.requestOperationTypeUniqueCode === 'pt.edp.request.operation.2'
              ) {
                GoogleAnalyticsEventsService.emitEvent(
                  'Pedidos',
                  'Detalhe Pedido de Esclarecimento',
                  'Acede ao detalhe de um Pedido de Esclarecimento'
                );
              }
            });
          },
          (err: any) => {
            console.error(err);
          }
        );
      },
      (err) => {
        console.error(err);
        this.router.navigate([''], { relativeTo: this.route });
      }
    );
  }

  checkIsRetailer(value) {
    const res = this.request.comments.find((obj) => {
      return obj.createdUserIsInternal === value;
    });
    if (res && res.createdUserIsInternal === '1') {
      return true;
    } else {
      return false;
    }
  }

  showCommentForm() {
    return this.request && this.request.detail.statusNewComment === '1';
  }

  showActions() {
    if (!this.request || !this.request.detail) {
      return false;
    }

    const FORBIDDEN_UNIQUE_CODES = ['pt.edp.request.status.4', 'pt.edp.request.status.3'];

    return !FORBIDDEN_UNIQUE_CODES.includes(this.request.detail.statusUniqueCode);
  }

  setPermissions(permissionsCreate, permissionsWrite) {
    this.createPermission = new RequestPermission(permissionsCreate, this.isRet, !this.isRet, false, true);
    this.writePermission = new RequestPermission(permissionsWrite, this.isRet, !this.isRet, true);
    this.internalNotesPermission = new RequestPermission(permissionsWrite, false, true, true);
  }

  synchronizeFiles(fileList, isList) {
    if (isList) {
      this.requestAttachments = fileList;
    } else {
      this.fileList = fileList;
    }
  }

  fileIsInvalid(fileIsInvalid) {
    this.fileTooLarge = fileIsInvalid;
  }

  deleteFile(fileId: string) {
    const confirmDialog = this.dialog.open(GenericModalComponent, {
      data: {
        modalTitle: 'Atenção',
        modalText: 'Tem a certeza que pretende apagar o anexo ?',
        btnConfirmText: 'Sim',
        btnCancelText: 'Não',
      },
      disableClose: false,
    });
    confirmDialog.afterClosed().subscribe((result) => {
      if (result === true) {
        this.requestService.deleteAttachment(fileId).subscribe((data) => {
          this.ngOnInit();
        });
      }
    });
  }

  downloadFile(file: any) {
    this.requestService.downloadAttachment(file.fileId).subscribe(
      (data) => {},
      (err) => {
        console.error(`error : ${err.error}`);
      }
    );
  }

  cancelRequest(detail: any) {
    const confirmDialog = this.dialog.open(GenericModalComponent, {
      data: {
        modalTitle: 'Atenção',
        modalText: 'Tem a certeza que pretende anular o Pedido ' + detail.requestMainId + '?',
        btnConfirmText: 'Sim',
        btnCancelText: 'Não',
      },
      disableClose: false,
    });
    confirmDialog.afterClosed().subscribe((result) => {
      if (result === true) {
        this.requestService.updateStatusReject(detail.requestUniqueCode).subscribe(
          (data: any) => {
            this.navigateToList();
          },
          (err: any) => {
            console.error(err);
          }
        );
      }
    });
  }

  sendComment(internal) {
    const form = internal ? this.sendInternalCommentForm.value.internalnotes : this.sendCommentForm.value.comment;
    this.insertComment(form, internal);
  }

  insertComment(comment, internal, orign?) {
    this.requestService.insertComment(this.request.detail.requestUniqueCode, comment, internal).subscribe(
      (data: any) => {
        if (orign !== 'popup') {
          if (internal === 1) {
            this.successMessageInternal();
          } else {
            if (this.fileList.length > 0) {
              this.requestService
                .insertCommentAttachments(
                  this.fileList,
                  data.Result.lastInsertId,
                  this.request.detail.requestUniqueCode
                )
                .subscribe(
                  (attachRes) => {
                    this.fileList = [];
                    this.successMessageComment();
                  },
                  (err) => {
                    this.errorMessageComment();
                    console.error(err);
                  }
                );
            } else {
              this.successMessageComment();
            }
          }
        }
        this.clearComment(internal);
      },
      (err: any) => {
        this.errorMessageCommentAttach();
        console.error(err);
      }
    );
  }

  clearComment(internal) {
    const form = !internal ? this.sendCommentForm : this.sendInternalCommentForm;
    this.fileList = [];
    form.reset();
  }

  operationChildren(operation: string) {
    this.requestService.getOperationChildren(operation).subscribe(
      (data: any) => {},
      (err: any) => {
        console.error(err);
      }
    );
  }

  // SUBMIT TYPE CHANGE REQUEST
  submitUpdateType(operationUniqueCode: string, voltageUniqueCode: string) {
    this.requestService
      .updateRequestOperation(this.request.detail.requestUniqueCode, operationUniqueCode, voltageUniqueCode)
      .subscribe(
        (data: any) => {
          this.successMessage();
        },
        (err: any) => {
          this.errorMessage();
        }
      );
  }

  // SUBMIT REALOCATE REQUEST
  submitRealocate() {
    this.requestService.realocate(this.request.detail.requestUniqueCode).subscribe(
      (data: any) => {
        this.successMessage();
      },
      (err: any) => {
        this.errorMessage();
      }
    );
  }

  /**
   * Return comment unique code to send to insert comment attachments
   * @param data result of change status
   * @returns requestCommentUniqueId or lastInsertId
   */
  extractCommentUniqueCode(data: any) {
    if (data && data.Result && data.Result.Collection && data.Result.Collection.length) {
      return data.Result.Collection[0].requestCommentUniqueId;
    }

    return data.Result.lastInsertId;
  }

  // SUBMIT STATUS REQUEST
  submitChangeStatus(status, note, files?) {
    this.requestService.changeStatus(this.request.detail.requestUniqueCode, status, note).subscribe(
      (data: any) => {
        if (
          data.Result &&
          data.Result.Collection[0] &&
          !data.Result.Collection[0].requestCommentPrivate &&
          files &&
          files.length > 0
        ) {
          const requestCommentUniqueId = this.extractCommentUniqueCode(data);
          this.requestService
            .insertCommentAttachments(files, requestCommentUniqueId, this.request.detail.requestUniqueCode)
            .subscribe(
              (attachRes) => {
                this.successMessage();
              },
              (err) => {
                this.errorMessage();
                console.error(err);
              }
            );
        } else {
          // When there's no file or it's request internal comment
          this.successMessage();
        }
      },
      (err: any) => {
        this.errorMessage();
      }
    );
  }

  submitReOpen(detail) {
    this.requestService.updateReopenRequest(this.request.detail.requestUniqueCode, detail.notes).subscribe(
      (data: any) => {
        this.successMessage();
      },
      (err: any) => {
        this.errorMessage();
      }
    );
  }

  closeRequestDetail() {
    this.navigateToList();
  }

  navigateToList() {
    if (!this.isRet) {
      this.router.navigateByUrl('/request/request-edp');
    } else if (this.requestType === 'o') {
      this.router.navigateByUrl('/request/request-operation');
    } else if (this.requestType === 'c') {
      this.router.navigateByUrl('/request/request-clarification');
    }
  }

  /***********************************************************/
  /** ****** OPEN MODAL - TODO:CONFIG DATA & COMPONENT */
  /***********************************************************/

  // OPEN ATTACHMENT MODAL

  // OPEN REQUEST MODAL
  openRequestDialog(component) {
    const requestDialog = this.dialog.open(component, {
      data: this.dataRequestModal,
      disableClose: false,
    });
    requestDialog.afterClosed().subscribe((result) => {
      if (result.event === true) {
        // CLOSE MODAL CLICK "btnConfirmText"
        if (this.dataRequestModal.changeType) {
          const lastType = result.data.type2;
          const voltage = result.data.voltage;
          const notes = result.data.notes;
          if (notes !== '') {
            this.insertComment(notes, 1, 'popup');
          }
          this.submitUpdateType(lastType, voltage);
          this.closeRequestDetail();
        } else if (this.dataRequestModal.changeStatus) {
          this.submitChangeStatus(result.data.status, result.data.notes, result.data.files);
        } else if (this.dataRequestModal.reOpen) {
          this.submitReOpen(result.data);
        } else {
          this.submitRealocate();
          this.closeRequestDetail();
        }
      } else if (result.event === false) {
        // CLOSE MODAL CLICK "btnCancelText"
      } else {
        // CLOSE MODAL CLICK "mat-dialog-close"
      }
    });
  }

  // OPEN GENERIC MODAL
  openGenericDialog(component) {
    const confirmDialog = this.dialog.open(component, {
      data: this.dataGenericModal,
      disableClose: false,
    });
    confirmDialog.afterClosed().subscribe((result) => {
      if (result === true) {
        this.ngOnInit();
      }
    });
  }

  /********************************************/
  /** ****** MESSAGES - SUCCESS & ERROR */
  /********************************************/

  // ACTION CHANGE TYPE
  changeType(detail) {
    this.dataRequestModal = {
      modalTitle: 'Alterar Tipo do Pedido ' + detail.requestMainId + '',
      btnConfirmText: 'Submeter',
      btnCancelText: 'Cancelar',
      infoDetail: detail,
      changeType: true,
    };
    this.openRequestDialog(RequestModalComponent);
  }

  // ACTION CHANGE STATUS
  changeStatus(detail) {
    this.dataRequestModal = {
      modalTitle: 'Alterar Estado do Pedido ' + detail.requestMainId + '',
      btnConfirmText: 'Submeter',
      btnCancelText: 'Cancelar',
      infoDetail: detail,
      changeStatus: true,
      allStatus: this.request.possibleStatus,
    };
    this.openRequestDialog(RequestModalComponent);
  }

  // ACTION REALOCATE
  changeRealocate(detail) {
    this.dataRequestModal = {
      modalTitle: 'Realocar Pedido ' + detail.requestMainId + '',
      modalText: 'Deseja alocar este pedido à equipa da EDP?',
      btnConfirmText: 'Sim',
      btnCancelText: 'Não',
      infoDetail: detail,
      changeUser: true,
    };
    this.openRequestDialog(RequestModalComponent);
  }

  // ACTION CHANGE STATUS
  seeAttachmentsList(detail) {
    this.dataRequestModal = {
      modalTitle: 'Lista de Anexos',
      btnConfirmText: 'Submeter',
      btnCancelText: 'Cancelar',
      infoDetail: detail,
      openListAttachments: true,
    };
    this.openRequestDialog(RequestModalComponent);
  }

  // ACTION REOPEN REQUEST
  reOpenRequest(detail: any) {
    if (this.isRet) {
      this.dataRequestModal = {
        modalTitle: 'Reabrir Pedido ' + detail.requestMainId + '',
        modalText: 'Deseja reabrir este pedido para a equipa da EDP?',
        btnConfirmText: 'Sim',
        btnCancelText: 'Não',
        infoDetail: detail,
        reOpen: true,
      };
      this.openRequestDialog(RequestModalComponent);
    } else {
      this.dataReOpenRequestModal = {
        modalTitle: 'Reabrir Pedido',
        modalText: 'Tem a certeza que pretende reabrir o pedido?',
        btnConfirmText: 'Sim',
        btnCancelText: 'Não',
      };
      const confirmDialog = this.dialog.open(GenericModalComponent, {
        data: this.dataReOpenRequestModal,
        disableClose: false,
      });
      confirmDialog.afterClosed().subscribe((result) => {
        if (result === true) {
          this.requestService.updateReopenRequest(detail.requestUniqueCode).subscribe(
            (data: any) => {
              this.successMessage();
            },
            (err: any) => {
              this.errorMessage();
            }
          );
        }
      });
    }
  }

  // ERROR MESSAGE GENERIC
  errorMessage() {
    this.dataGenericModal = {
      modalTitle: 'Atenção',
      modalText: 'Houve um erro na alteração do pedido. Tente novamente.',
      btnConfirmText: 'OK',
      error: true,
    };
    this.openGenericDialog(GenericModalComponent);
  }

  // SUCCESS MESSAGE GENERIC
  successMessage() {
    this.dataGenericModal = {
      modalTitle: 'Sucesso',
      modalText: 'A sua alteração ao pedido foi feita com sucesso!',
      btnConfirmText: 'Ok',
      success: true,
    };
    this.openGenericDialog(GenericModalComponent);
  }

  // ERROR MESSAGE COMMENT
  errorMessageComment() {
    this.dataGenericModal = {
      modalTitle: 'Atenção',
      modalText: 'Houve um erro na inserção da resposta. Tente novamente.',
      btnConfirmText: 'OK',
      error: true,
    };
    this.openGenericDialog(GenericModalComponent);
  }

  // ERROR MESSAGE COMMENT ATTACH
  errorMessageCommentAttach() {
    this.dataGenericModal = {
      modalTitle: 'Atenção',
      modalText: 'Houve um erro na inserção dos anexos da resposta. Tente novamente.',
      btnConfirmText: 'OK',
      error: true,
    };
    this.openGenericDialog(GenericModalComponent);
  }

  // SUCCESS MESSAGE INTERNAL NOTE
  successMessageInternal() {
    this.dataGenericModal = {
      modalTitle: 'Sucesso',
      modalText: 'Nota interna inserida com sucesso!',
      btnConfirmText: 'Ok',
      success: true,
    };
    this.openGenericDialog(GenericModalComponent);
  }

  // SUCCESS MESSAGE COMMENT
  successMessageComment() {
    this.dataGenericModal = {
      modalTitle: 'Sucesso',
      modalText: 'Resposta inserida com sucesso!',
      btnConfirmText: 'Ok',
      success: true,
    };
    this.openGenericDialog(GenericModalComponent);
  }
}
